import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { isBoxConfigLockUpdateAllowed } from '@flow/libs/agreement/box-config-lock-update-permissions';
import { isBoxConfigUpdateAllowed } from '@flow/libs/agreement/box-config-update-permissions';
import { isBoxCreateAllowed } from '@flow/libs/agreement/box-add-permissions';
import { isBoxDataCreateAllowed } from '@flow/libs/agreement/box-data-create-permissions';
import { isBoxDataEditAllowed } from '@flow/libs/agreement/box-data-edit-permissions';
import { isBoxDataRemoveAllowed } from '@flow/libs/agreement/box-data-remove-permissions';
import { isBoxDataReorderAllowed } from '@flow/libs/agreement/box-data-reorder-permissions';
import { isBoxDataSharedValueUpdateAllowed } from '@flow/libs/agreement/box-data-shared-value-update-permissions';
import { isBoxDataValueUpdateAllowed } from '@flow/libs/agreement/box-data-value-update-permissions';
import { isBoxRemoveAllowed } from '@flow/libs/agreement/box-remove-permissions';
import { isBoxReorderAllowed } from '@flow/libs/agreement/box-reorder-permissions';

import useAgreement from '@app/hooks/use-agreement';
import useCurrentBox from '@app/hooks/use-current-box';

import type { Acl } from 'types/acl';
import type { BoxDataPermissionSubset as BoxData } from '@flow/libs/agreement/permission-types';

type BoxPermissionPropsContext = {
  isAllowedToAddBox: boolean;
  isAllowedToAddBoxData: boolean;
  isAllowedToEditBoxData: boolean;
  isAllowedToRemoveBox: boolean;
  isAllowedToReorderBoxData: boolean;
  isAllowedToReorderBoxes: boolean;
  isAllowedToUpdateBoxConfig: boolean;
  isAllowedToUpdateBoxConfigLock: boolean;
  getIsAllowedToRemoveBoxData: (boxData?: BoxData) => boolean;
  getIsAllowedToUpdateDataValue: (boxData?: BoxData) => boolean;
  getIsAllowedToUpdateSharedDataValue: (boxData?: BoxData) => boolean;
} | null;

export const BoxPermissionPropsContext = createContext<BoxPermissionPropsContext>(null);

type BoxPermissionPropsProviderProps = {
  agreementId: number;
  boxId: number;
  children: ReactNode;
};

export function BoxPermissionPropsProvider({
  agreementId,
  boxId,
  children,
}: BoxPermissionPropsProviderProps) {
  const agreement = useAgreement(agreementId);
  const box = useCurrentBox(boxId);

  const agreementPermissionSubset = useMemo(
    () => ({
      acl: agreement.acl as Acl,
    }),
    [
      agreement.acl,
    ],
  );

  const agreementPermissionValues = useMemo(
    () => ({
      isAllowedToAddBox: isBoxCreateAllowed(agreementPermissionSubset),
      isAllowedToReorderBoxes: isBoxReorderAllowed(agreementPermissionSubset),
    }),
    [
      agreementPermissionSubset,
    ],
  );

  const boxPermissionSubset = useMemo(
    () => ({
      id: box?.id,
      _id: box?._id,
      acl: box?.acl,
    }),
    [
      box?.id,
      box?._id,
      box?.acl,
    ],
  );

  const boxPermissionValues = useMemo(
    () => ({
      isAllowedToAddBoxData: isBoxDataCreateAllowed(boxPermissionSubset),
      isAllowedToEditBoxData: isBoxDataEditAllowed(boxPermissionSubset),
      isAllowedToRemoveBox: isBoxRemoveAllowed(boxPermissionSubset),
      isAllowedToReorderBoxData: isBoxDataReorderAllowed(boxPermissionSubset),
      isAllowedToUpdateBoxConfig: isBoxConfigUpdateAllowed(boxPermissionSubset),
      isAllowedToUpdateBoxConfigLock: isBoxConfigLockUpdateAllowed(boxPermissionSubset),
    }),
    [
      boxPermissionSubset,
    ],
  );

  const boxPermissionCallbacks = useMemo(
    () => ({
      getIsAllowedToRemoveBoxData: (boxData?: BoxData) =>
        isBoxDataRemoveAllowed(boxPermissionSubset, boxData),
      getIsAllowedToUpdateDataValue: (boxData?: BoxData) =>
        isBoxDataValueUpdateAllowed(boxPermissionSubset, boxData),
      getIsAllowedToUpdateSharedDataValue: (boxData?: BoxData) =>
        isBoxDataSharedValueUpdateAllowed(boxPermissionSubset, boxData),
    }),
    [
      boxPermissionSubset,
    ],
  );

  const contextValue = useMemo(
    () => ({
      ...agreementPermissionValues,
      ...boxPermissionValues,
      ...boxPermissionCallbacks,
    }),
    [
      agreementPermissionValues,
      boxPermissionCallbacks,
      boxPermissionValues,
    ],
  );

  return (
    <BoxPermissionPropsContext.Provider value={contextValue}>
      {children}
    </BoxPermissionPropsContext.Provider>
  );
}

export const useBoxPermissionProps = () => {
  const contextValue = useContext(BoxPermissionPropsContext);

  if (!contextValue) {
    throw new Error('useBoxPermissionProps should be used inside a BoxPermissionPropsProvider');
  }

  return contextValue;
};
